/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { StoryblokServerComponent, storyblokEditable } from '@storyblok/react/rsc'
import deepmerge from 'deepmerge'
import React from 'react'
import {
  MARK_BOLD,
  MARK_CODE,
  MARK_ITALIC,
  MARK_LINK,
  MARK_STRIKE,
  MARK_STYLED,
  MARK_TEXT_STYLE,
  MARK_UNDERLINE,
  NODE_HEADING,
  NODE_LI,
  NODE_OL,
  NODE_PARAGRAPH,
  NODE_QUOTE,
  NODE_TABLE,
  NODE_TABLE_CELL,
  NODE_TABLE_HEADER,
  NODE_TABLE_ROW,
  NODE_UL,
  RenderOptions,
  render,
  StoryblokRichtext,
} from 'storyblok-rich-text-react-renderer'

import { Link } from '@/i18n/navigation'

/** A description of the available properties for this component. */
type RichTextRendererProps = {
  /** The Storyblok rich text document to render. */
  document: StoryblokRichtext | unknown

  /** Additional options to pass to the renderer. */
  options?: RenderOptions
}

/**
 * A wrapper around the Storyblok rich text renderer that provides default resolvers for common elements.
 */
const RichTextRenderer: React.FC<RichTextRendererProps> = ({ document, options }) => {
  const defaultOptions: RenderOptions = {
    nodeResolvers: {
      [NODE_PARAGRAPH]: (children) => <p className={`mb-4`}>{children}</p>,
      [NODE_HEADING]: (children, { level }) => {
        const HeadingTag = `h${level}` as keyof React.JSX.IntrinsicElements
        const sizeClasses = {
          1: 'text-4xl lg:text-5xl',
          2: 'text-3xl lg:text-4xl',
          3: 'text-2xl',
          4: 'text-xl',
          5: 'text-lg',
          6: 'text-base',
        }
        return <HeadingTag className={`font-bold mb-4 ${sizeClasses[level]}`}>{children}</HeadingTag>
      },
      [NODE_OL]: (children) => <ol className={`list-decimal pl-8 mb-4`}>{children}</ol>,
      [NODE_UL]: (children) => <ul className={`list-disc pl-8 mb-4`}>{children}</ul>,
      [NODE_LI]: (children) => <li>{children}</li>,
      [NODE_QUOTE]: (children) => (
        <blockquote className={`border-l-4 border-gray-300 pl-4 italic mb-4`}>{children}</blockquote>
      ),
      [NODE_TABLE]: (children) => (
        <div className='w-full overflow-x-auto rounded-lg my-4'>
          <div className='min-w-full bg-white border border-gray-300 rounded-lg overflow-hidden'>
            <table className='min-w-full not-prose divide-y divide-gray-300 my-0'>{children}</table>
          </div>
        </div>
      ),
      [NODE_TABLE_ROW]: (children) => <tr className='divide-x divide-gray-300'>{children}</tr>,
      [NODE_TABLE_CELL]: (children, { colspan, rowspan, backgroundColor, colwidth }) => {
        const style: React.CSSProperties = {}
        if (backgroundColor) {
          style.backgroundColor = backgroundColor
        }
        if (colwidth && colwidth.length > 0) {
          const totalWidth = colwidth.reduce((sum, width) => sum + width, 0)
          if (totalWidth > 0) {
            style.width = `${totalWidth}px`
          }
        }

        return (
          <td
            colSpan={colspan}
            rowSpan={rowspan}
            style={style}
            className={'px-2 py-1 text-sm text-left font-normal align-top'}
          >
            {children}
          </td>
        )
      },
      [NODE_TABLE_HEADER]: (children, { colspan, rowspan, backgroundColor, colwidth }) => {
        const style: React.CSSProperties = {}
        if (backgroundColor) {
          style.backgroundColor = backgroundColor
        }
        if (colwidth && colwidth.length > 0) {
          const totalWidth = colwidth.reduce((sum, width) => sum + width, 0)
          if (totalWidth > 0) {
            style.width = `${totalWidth}px`
          }
        }
        return (
          <th
            colSpan={colspan}
            rowSpan={rowspan}
            style={style}
            className='px-3 pt-2 text-sm text-left font-semibold bg-gray-50 align-top'
          >
            {children}
          </th>
        )
      },
    },
    markResolvers: {
      [MARK_BOLD]: (children) => <strong className={'font-bold'}>{children}</strong>,
      [MARK_ITALIC]: (children) => <em className={'italic'}>{children}</em>,
      [MARK_STRIKE]: (children) => <span className={'line-through'}>{children}</span>,
      [MARK_UNDERLINE]: (children) => <span className={'underline'}>{children}</span>,
      [MARK_CODE]: (children) => <code className={'bg-gray-100 rounded-xs px-1 py-0.5'}>{children}</code>,
      [MARK_STYLED]: (children, { class: className }) => <span className={className || ''}>{children}</span>,
      [MARK_TEXT_STYLE]: (children, { color }) => <span style={{ color }}>{children}</span>,
      [MARK_LINK]: (children, { href, target }) => (
        <Link href={href!} target={target} className={'text-blue-600 hover:underline'}>
          {children}
        </Link>
      ),
    },
    defaultStringResolver: (string) => <>{string}</>,
    textResolver: (string) => string,
    defaultBlokResolver: (name, props) => {
      const blok = { ...props, component: name }
      return <StoryblokServerComponent blok={blok} key={props._uid} {...storyblokEditable(blok)} />
    },
  }

  return render(document, options ? deepmerge(defaultOptions, options) : defaultOptions)
}

export { RichTextRenderer }
